import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { ChatTranscriptItem, ReferencedFile } from "~/ai-chat/model/chat-message";
import type { SavedArtifactRef } from "~/ai-chat/model/conversation-artifacts";

interface AiChatContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  messages: ChatTranscriptItem[];
  appendMessage: (message: ChatTranscriptItem) => void;
  updateMessage: (id: string, updater: (item: ChatTranscriptItem) => ChatTranscriptItem) => void;
  // Roll back an optimistic message (e.g. the user turn of a send that failed
  // before producing any reply — its content goes back to the composer instead).
  removeMessage: (id: string) => void;
  clearMessages: () => void;
  // Load a persisted conversation's messages into the transcript (FE-3), or cut it
  // in place when a user turn is edited.
  replaceMessages: (items: ChatTranscriptItem[]) => void;
  // Resources saved from this chat, which the assistant doesn't necessarily link back.
  // Reset on every conversation switch (setActiveConversationId), nowhere else, so an
  // edit or a cleared transcript inside the same chat keeps them.
  savedArtifacts: SavedArtifactRef[];
  recordSavedArtifact: (artifact: SavedArtifactRef) => void;
  // Persisted-conversation id the transcript is bound to, or null for an
  // unsaved chat that hasn't been created on the backend yet.
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  // Same transcript, different persistence: the unsaved chat on screen just got its
  // server id (first send), or loses it again when that send fails before streaming.
  // Not a switch, so the chat's saved artifacts stay.
  bindConversationId: (id: string | null) => void;
  startNewConversation: () => void;
  // A conversation is "unsynced" between its creation on first send and its
  // turn finishing persistence — the local transcript is the source of truth
  // then, and hydrating from the server would wipe the live reply.
  markConversationUnsynced: (id: string) => void;
  markConversationSynced: (id: string) => void;
  isConversationUnsynced: (id: string) => boolean;
  // Conversations whose turn is streaming right now (shared across the app so the
  // tab status dots reflect live "processing" state, not the cached list).
  runningConversationIds: Set<string>;
  markConversationRunning: (id: string) => void;
  markConversationIdle: (id: string) => void;
  // Chat-tab strip + in-flight stream controllers live here (not in the tab/send hooks)
  // so they survive a ChatPanel remount — popping the widget out/in re-parents the panel,
  // which would otherwise blank the open-tabs bar and abort every running stream.
  tabInitialKey: string;
  tabOpenIds: string[];
  setTabOpenIds: React.Dispatch<React.SetStateAction<string[]>>;
  tabEmptyKeys: string[];
  setTabEmptyKeys: React.Dispatch<React.SetStateAction<string[]>>;
  // Titles of unsaved tabs by client key. `chatTitle` only holds the on-screen chat's, so an
  // off-screen unsaved tab keeps its name here until its first send creates the conversation.
  tabEmptyTitles: Record<string, string | null>;
  setTabEmptyTitles: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  tabActiveEmptyKey: string | null;
  setTabActiveEmptyKey: React.Dispatch<React.SetStateAction<string | null>>;
  chatControllersRef: { current: Map<string, AbortController> };
  newChatControllerRef: { current: AbortController | null };
  activeConversationIdRef: { current: string | null };
  newChatPending: boolean;
  setNewChatPending: React.Dispatch<React.SetStateAction<boolean>>;
  // Monotonic counter bumped on every navigation (conversation switch / new
  // chat). A send captures it at start; async completions (e.g. publishing a
  // just-created conversation id) compare before touching the active state, so
  // a slow request can never relabel a transcript the user has moved away from.
  getConversationEpoch: () => number;
  // A failed send puts the typed message here so the composer can restore it
  // instead of discarding it.
  restoredDraft: string | null;
  setRestoredDraft: (content: string | null) => void;
  // User-facing conversation title. Before the conversation is created it seeds
  // the create title; once persisted it tracks renames (set optimistically and
  // reverted on rename failure).
  chatTitle: string | null;
  setChatTitle: (title: string | null) => void;
  // The user-picked model id (F12), or null to let the server pick its default.
  selectedModelId: string | null;
  setSelectedModelId: (id: string | null) => void;
  isAvailable: boolean;
  markUnavailable: () => void;
  hasMcpTools: boolean;
  markMcpToolsAvailable: () => void;
  notebookContext: string | null;
  setNotebookContext: (ctx: string | null) => void;
  onInsertCode: ((code: string) => void) | null;
  setOnInsertCode: (fn: ((code: string) => void) | null) => void;
  // Resolves extra system-prompt context for one specific request (e.g. the parameter schema of the
  // job function a prompt names). Set by the notebook host; awaited at generation time.
  resolvePromptContext: ((prompt: string) => Promise<string | null>) | null;
  setResolvePromptContext: (fn: ((prompt: string) => Promise<string | null>) | null) => void;
  referencedFiles: ReferencedFile[];
  addReferencedFiles: (files: ReferencedFile[]) => void;
  removeReferencedFile: (resourceId: string) => void;
  clearReferencedFiles: () => void;
  // The resource this transcript was saved as this session, so a re-share reopens that resource instead of
  // saving a duplicate. Reset when the transcript changes (new chat / switch). Null until saved; the backend
  // conversation link (read from the list cache) covers the cross-session case after a reload.
  savedTranscriptResourceId: string | null;
  setSavedTranscriptResourceId: (resourceId: string | null) => void;
}

const AiChatContext = React.createContext<AiChatContextValue | null>(null);

export function useAiChat() {
  const context = useContext(AiChatContext);
  if (!context) {
    throw new Error("useAiChat must be used within an AiChatProvider.");
  }
  return context;
}

export function useAiChatOptional() {
  return useContext(AiChatContext);
}

interface AiChatProviderProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export function AiChatProvider({ children, defaultOpen = false }: AiChatProviderProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [messages, setMessages] = useState<ChatTranscriptItem[]>([]);
  const [savedArtifacts, setSavedArtifacts] = useState<SavedArtifactRef[]>([]);
  const [isAvailable, setIsAvailable] = useState(true);
  const [hasMcpTools, setHasMcpTools] = useState(false);
  const [notebookContext, setNotebookContext] = useState<string | null>(null);
  const [onInsertCode, setOnInsertCode] = useState<((code: string) => void) | null>(null);
  const [resolvePromptContext, setResolvePromptContext] = useState<((prompt: string) => Promise<string | null>) | null>(
    null,
  );
  const [referencedFiles, setReferencedFiles] = useState<ReferencedFile[]>([]);
  const [savedTranscriptResourceId, setSavedTranscriptResourceId] = useState<string | null>(null);
  const [activeConversationId, setActiveConversationIdState] = useState<string | null>(null);
  const [chatTitle, setChatTitle] = useState<string | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [restoredDraft, setRestoredDraft] = useState<string | null>(null);
  const unsyncedConversationsRef = useRef(new Set<string>());
  const conversationEpochRef = useRef(0);
  const [runningConversationIds, setRunningConversationIds] = useState<Set<string>>(() => new Set());

  const [tabInitialKey] = useState(() => crypto.randomUUID());
  const [tabOpenIds, setTabOpenIds] = useState<string[]>([]);
  const [tabEmptyKeys, setTabEmptyKeys] = useState<string[]>(() =>
    activeConversationId === null ? [tabInitialKey] : [],
  );
  const [tabEmptyTitles, setTabEmptyTitles] = useState<Record<string, string | null>>({});
  const [tabActiveEmptyKey, setTabActiveEmptyKey] = useState<string | null>(() =>
    activeConversationId === null ? tabInitialKey : null,
  );
  const chatControllersRef = useRef(new Map<string, AbortController>());
  const newChatControllerRef = useRef<AbortController | null>(null);
  const [newChatPending, setNewChatPending] = useState(false);
  // Live mirror of the on-screen conversation, owned here (not in useChatSend) so a stream
  // that outlives a ChatPanel remount still reads the current active id — otherwise its
  // isViewing() would be frozen to the remounted-away id and paint into the wrong transcript.
  const activeConversationIdRef = useRef(activeConversationId);

  // Abort in-flight streams only when the chat feature itself unmounts (app teardown /
  // flag flip), never on a ChatPanel remount — that distinction is why the controllers
  // live at the provider level.
  useEffect(
    () => () => {
      for (const controller of chatControllersRef.current.values()) controller.abort();
      newChatControllerRef.current?.abort();
    },
    [],
  );

  const markConversationRunning = useCallback((id: string) => {
    setRunningConversationIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);
  const markConversationIdle = useCallback((id: string) => {
    setRunningConversationIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const getConversationEpoch = useCallback(() => conversationEpochRef.current, []);

  // Every change of the id the transcript is bound to bumps the epoch, so an async
  // completion started against an older binding can detect it (this includes the
  // first-send publish: a second send racing the same create must not relabel it).
  const bindConversationId = useCallback((id: string | null) => {
    conversationEpochRef.current += 1;
    // Sync the ref synchronously (the effect below lags a render) so a stream's first
    // deltas immediately recognise their conversation as the one on screen and paint.
    activeConversationIdRef.current = id;
    setActiveConversationIdState(id);
    // Deliberately does NOT clear savedTranscriptResourceId: this handles the first-send null→id bind and
    // rollback of the SAME transcript, so a chat saved before its conversation existed must stay linked. A
    // real switch goes through setActiveConversationId, which clears it.
  }, []);

  const setActiveConversationId = useCallback(
    (id: string | null) => {
      bindConversationId(id);
      setSavedArtifacts([]);
      // A switch puts a different transcript on screen; its saved-resource link comes from the backend
      // (list cache), not this just-cleared session memory.
      setSavedTranscriptResourceId(null);
    },
    [bindConversationId],
  );

  // Keep the ref in step with any other path that changes the state (e.g. React
  // batching / future setters); setActiveConversationId already syncs it eagerly.
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const markConversationUnsynced = useCallback((id: string) => {
    unsyncedConversationsRef.current.add(id);
  }, []);
  const markConversationSynced = useCallback((id: string) => {
    unsyncedConversationsRef.current.delete(id);
  }, []);
  const isConversationUnsynced = useCallback((id: string) => unsyncedConversationsRef.current.has(id), []);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const appendMessage = useCallback((message: ChatTranscriptItem) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  // Used by streaming to grow an in-flight assistant message as deltas arrive.
  const updateMessage = useCallback((id: string, updater: (item: ChatTranscriptItem) => ChatTranscriptItem) => {
    setMessages((prev) => prev.map((item) => (item.id === id ? updater(item) : item)));
  }, []);

  const removeMessage = useCallback((id: string) => {
    setMessages((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setReferencedFiles([]);
  }, []);

  const replaceMessages = useCallback((items: ChatTranscriptItem[]) => {
    setMessages(items);
  }, []);

  const recordSavedArtifact = useCallback((artifact: SavedArtifactRef) => {
    setSavedArtifacts((prev) =>
      prev.some((saved) => saved.resourceId === artifact.resourceId) ? prev : [...prev, artifact],
    );
  }, []);

  const startNewConversation = useCallback(() => {
    // Leaving discards the local transcript, so the departing conversation's
    // unsynced protection is moot — releasing it lets a later re-open hydrate
    // the server copy instead of showing whatever the transcript holds by then.
    if (activeConversationId !== null) {
      unsyncedConversationsRef.current.delete(activeConversationId);
    }
    setActiveConversationId(null);
    setMessages([]);
    setReferencedFiles([]);
    setSavedTranscriptResourceId(null);
    setChatTitle(null);
    // Each new chat starts on the server default; a prior per-chat model choice doesn't carry over.
    setSelectedModelId(null);
  }, [activeConversationId, setActiveConversationId]);

  const markUnavailable = useCallback(() => setIsAvailable(false), []);
  const markMcpToolsAvailable = useCallback(() => setHasMcpTools(true), []);

  // useState setter for a function value requires the functional-update form to avoid
  // React treating the function itself as a state initializer.
  const handleSetOnInsertCode = useCallback((fn: ((code: string) => void) | null) => {
    setOnInsertCode(() => fn);
  }, []);

  const handleSetResolvePromptContext = useCallback((fn: ((prompt: string) => Promise<string | null>) | null) => {
    setResolvePromptContext(() => fn);
  }, []);

  const addReferencedFiles = useCallback((files: ReferencedFile[]) => {
    setReferencedFiles((prev) => {
      const byId = new Map(prev.map((file) => [file.resourceId, file]));
      for (const file of files) {
        byId.set(file.resourceId, file);
      }
      return [...byId.values()];
    });
  }, []);

  const removeReferencedFile = useCallback((resourceId: string) => {
    setReferencedFiles((prev) => prev.filter((file) => file.resourceId !== resourceId));
  }, []);

  const clearReferencedFiles = useCallback(() => setReferencedFiles([]), []);

  const value = useMemo<AiChatContextValue>(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      messages,
      appendMessage,
      updateMessage,
      removeMessage,
      clearMessages,
      replaceMessages,
      savedArtifacts,
      recordSavedArtifact,
      activeConversationId,
      setActiveConversationId,
      bindConversationId,
      startNewConversation,
      markConversationUnsynced,
      markConversationSynced,
      isConversationUnsynced,
      runningConversationIds,
      markConversationRunning,
      markConversationIdle,
      tabInitialKey,
      tabOpenIds,
      setTabOpenIds,
      tabEmptyKeys,
      setTabEmptyKeys,
      tabEmptyTitles,
      setTabEmptyTitles,
      tabActiveEmptyKey,
      setTabActiveEmptyKey,
      chatControllersRef,
      newChatControllerRef,
      activeConversationIdRef,
      newChatPending,
      setNewChatPending,
      getConversationEpoch,
      restoredDraft,
      setRestoredDraft,
      chatTitle,
      setChatTitle,
      selectedModelId,
      setSelectedModelId,
      isAvailable,
      markUnavailable,
      hasMcpTools,
      markMcpToolsAvailable,
      notebookContext,
      setNotebookContext,
      onInsertCode,
      setOnInsertCode: handleSetOnInsertCode,
      resolvePromptContext,
      setResolvePromptContext: handleSetResolvePromptContext,
      referencedFiles,
      addReferencedFiles,
      removeReferencedFile,
      clearReferencedFiles,
      savedTranscriptResourceId,
      setSavedTranscriptResourceId,
    }),
    [
      isOpen,
      open,
      close,
      toggle,
      messages,
      appendMessage,
      updateMessage,
      removeMessage,
      clearMessages,
      replaceMessages,
      savedArtifacts,
      recordSavedArtifact,
      activeConversationId,
      setActiveConversationId,
      bindConversationId,
      startNewConversation,
      markConversationUnsynced,
      markConversationSynced,
      isConversationUnsynced,
      runningConversationIds,
      markConversationRunning,
      markConversationIdle,
      tabInitialKey,
      tabOpenIds,
      setTabOpenIds,
      tabEmptyKeys,
      setTabEmptyKeys,
      tabEmptyTitles,
      setTabEmptyTitles,
      tabActiveEmptyKey,
      setTabActiveEmptyKey,
      chatControllersRef,
      newChatControllerRef,
      activeConversationIdRef,
      newChatPending,
      setNewChatPending,
      getConversationEpoch,
      restoredDraft,
      chatTitle,
      selectedModelId,
      isAvailable,
      markUnavailable,
      hasMcpTools,
      markMcpToolsAvailable,
      notebookContext,
      onInsertCode,
      handleSetOnInsertCode,
      resolvePromptContext,
      handleSetResolvePromptContext,
      referencedFiles,
      addReferencedFiles,
      removeReferencedFile,
      clearReferencedFiles,
      savedTranscriptResourceId,
    ],
  );

  return <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>;
}
